import { useState, useMemo, useEffect, ChangeEvent } from 'react'

import { FilterContainerOnClickTagParam } from '@/containers'
import { FilterContainerSelected } from '@/containers/filter'
import { PostsContainerProps } from '@/containers/posts'
import { usePagination } from '@/hooks'
import { Article } from '@/service/api/blog'

export const usePosts = ({ posts, categories }: PostsContainerProps) => {
  const { articles } = posts

  const { currentPage, setCurrentPage, totalPages, startIndex, endIndex } =
    usePagination({
      pageSize: 10,
    })

  const postsData = useMemo<Article[]>(() => articles, [articles])

  const [categoriesData, setCategoriesData] = useState(categories)
  const [search, setSearch] = useState('')

  const [filterByCategory, setFilterByCategory] =
    useState<FilterContainerOnClickTagParam>('')

  const [filterBy, setFilterBy] = useState<FilterContainerSelected>(
    'new' as FilterContainerSelected,
  )

  const onChange = (e: ChangeEvent<HTMLInputElement>) =>
    setSearch(e.target.value)

  const postsFiltered = useMemo<Article[]>(() => {
    let newPosts = [...postsData]
    if (filterBy === 'popular') {
      return newPosts.sort((a, b) => b.amountVisitors - a.amountVisitors)
    }

    if (filterBy === 'new') {
      newPosts.sort((a: any, b: any) => {
        const aDate = new Date(a.publishedAt)
        const bDate = new Date(b.publishedAt)

        return bDate.getTime() - aDate.getTime()
      })
    }

    if (filterByCategory) {
      newPosts = newPosts.filter((item) => item.tags.includes(filterByCategory))
    }

    if (search.trim().length > 0) {
      const searchTrim = search.trim()
      newPosts = newPosts.filter(
        (item) =>
          item.title.includes(searchTrim) ||
          item.about.includes(searchTrim) ||
          item.content.includes(searchTrim),
      )
    }

    return newPosts
  }, [filterBy, postsData, filterByCategory, search])

  const postsToRender = useMemo(
    () => postsFiltered.slice(startIndex, endIndex),
    [postsFiltered, startIndex, endIndex],
  )

  const numberPages = useMemo(
    () => totalPages({ totalItems: postsFiltered.length }),
    [postsFiltered.length, totalPages],
  )

  useEffect(() => {
    setCurrentPage(1)
  }, [filterBy, filterByCategory, setCurrentPage])

  return {
    postsData,
    filterBy,
    setFilterBy,
    categoriesData,
    setFilterByCategory,
    filterByCategory,
    setCategoriesData,
    currentPage,
    setCurrentPage,
    numberPages,
    postsToRender,
    onChange,
    search,
  }
}
